import { Check } from '@sparkware/app-utils';
import { observable, action, computed, makeObservable } from 'mobx';
import { ServiceBase } from '../ServiceBase';
import { IVideoPlayer } from './video-players/video-player.interface';
import { IServiceFactoryExtended } from '../service-factory-extended.interface';
import { IVideoPlayingService } from './video-playing.service.interface';
import {GameInfo} from "../games/game-info";
import {ISwipeLogicHandler} from "../arenas/models/swiper-logic-handler/swiper-logic-handler.interface";

export abstract class VideoPlayingServiceBase extends ServiceBase implements IVideoPlayingService {

    public abstract gaCategory: string;

    @observable.ref
    isSupported: boolean = null;

    @action.bound
    private _setIsSupported(isSupported: boolean): void {
        this.isSupported = isSupported;
    }

    @observable.ref
    isVideoMuted: boolean = true;

    @action.bound
    private _setIsVideoMuted(isVideoMuted: boolean): void {
        this.isVideoMuted = isVideoMuted;
    }

    @observable.ref
    isAppVisible: boolean = true;

    @action.bound
    private _setIsAppVisible(isAppVisible: boolean): void {
        this.isAppVisible = isAppVisible;
    }

    @computed
    get isDisabledPrelogin(): boolean {
        if (this.services.user.isAuthorized) {
            return false;
        }

        const currentRegulationCode = this.services.regulation.getRegulationCode();
        const disableForRegulationPreLogin = this.services.configuration.servicesConfigs.LiveVideoStream.disableForRegulationPreLogin[currentRegulationCode];
        return !Check.isNullOrUndefined(disableForRegulationPreLogin) && disableForRegulationPreLogin;
    }

    //Swiper Handler for the orbit swiper handling the current playing video.
    //This implementation breaks if in the future there will be > 1 swipers playing the same type of video in the same page.
    //But for now, we need this to swipe to & start the next video after the current one finishes playing.
    private _swiperHandler: ISwipeLogicHandler | null = null;

    public setSwiperHandler(value: ISwipeLogicHandler): void {
        this._swiperHandler = value;
    }

    public getSwiperHandler(): ISwipeLogicHandler | null {
        return this._swiperHandler;
    }

    constructor (services: IServiceFactoryExtended) {
        super(services);

        makeObservable(this);

        this._init();
        this.services.sdk.events.onAppPaused(() => {
            this._setIsAppVisible(false);
        });
        this.services.sdk.events.onAppResumed(() => {
            this._setIsAppVisible(true);
        });
        services.sdk.events.onGameOpen(() => {
            this._setIsVideoMuted(true);
        });
    }

    private async _init() {
        const isSupported = await this._checkIfSupported();

        if (!isSupported) {
            this.services.sdk.structuredLoggingService
                .getLogger()
                .addCategory(this.gaCategory)
                .addAction('VideoStreamNotSupported')
                .info();
        }

        this._setIsSupported(isSupported);
    }

    protected abstract _checkIfSupported(): Promise<boolean>;

    public abstract getVideoPlayer(gameInfo: GameInfo, videoReference: HTMLVideoElement): Promise<IVideoPlayer>;

    toggleVideoSound() {
        this._setIsVideoMuted(!this.isVideoMuted);
        this.services.analytics.sendOrbitMarketingEvent(this.gaCategory, 'SOUND_CLICK', `${this.isVideoMuted ? "OFF" : "ON"}`);
    }
}